import React, { useEffect, useRef } from 'react';
import logo from '../../assets/images/artwer/logo-dark-text.png';

// ART-WER contact section — no form, no action buttons (per request).
// #contact sits on the data block (not the map), so every "Offerte aanvragen" /
// "Plan een opname" CTA lands on the phone/mail details; the map keeps its own
// #werkgebied anchor above it.
const WHATSAPP_NUMBER = '31616136942'; // +31 6 16 13 69 42

const ContactMain = () => {
  const mapRef = useRef(null);

  // Service-area map: whole NL in view, main working region highlighted around
  // Biddinghuizen. Leaflet (JS + CSS) is imported lazily and only once the map
  // scrolls near the viewport, so it stays off the critical loading path.
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    let map;
    let cancelled = false;

    const init = async () => {
      const [{ default: L }] = await Promise.all([
        import('leaflet'),
        import('leaflet/dist/leaflet.css'),
      ]);
      if (cancelled || !mapRef.current || mapRef.current._leaflet_id) return;
      const bidd = [52.4536, 5.6939];
      map = L.map(el, { scrollWheelZoom: false, attributionControl: false }).setView([52.12, 5.3], 7);
      // ODbL requires visible attribution; top-right so the floating contact
      // card can't cover it on small screens.
      L.control
        .attribution({ position: 'topright', prefix: false })
        .addAttribution('&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors')
        .addTo(map);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18 }).addTo(map);
      L.circle(bidd, { radius: 78000, color: '#E63A27', weight: 2, opacity: 0.85, fillColor: '#E63A27', fillOpacity: 0.10 }).addTo(map);
      L.circleMarker(bidd, { radius: 7, color: '#fff', weight: 2, fillColor: '#E63A27', fillOpacity: 1 })
        .addTo(map)
        .bindTooltip('ART-WER · Biddinghuizen', { direction: 'top' });
    };

    let io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            init();
          }
        },
        { rootMargin: '600px 0px' }
      );
      io.observe(el);
    } else {
      init();
    }

    return () => {
      cancelled = true;
      if (io) io.disconnect();
      if (map) map.remove();
    };
  }, []);

  return (
    <>
      {/* Service-area map — regio Biddinghuizen binnen NL */}
      <section className="google-map-one" id="werkgebied">
        <div
          ref={mapRef}
          className="google-map__one artwer-servicemap"
          role="region"
          aria-label="Kaart van het werkgebied: regio Biddinghuizen, Flevoland en Midden-Nederland"
        />
      </section>

      <section className="contact-page" id="contact">
        <div className="container">
          <div className="contact-page__inner">
            <img src={logo} className="artwer-contact-logo" alt="ART-WER Aannemingsbedrijf" />
            <p className="contact-page__sub-title">Contact</p>
            <h2 className="contact-page__title">Eén telefoontje. Eén opname. Eén partner.</h2>
            <p className="artwer-contact-lead">
              Bel of mail vandaag nog voor een vrijblijvende opname op locatie. U spreekt direct de
              vakman — geen wachtlijst, geen tussenpersoon.
            </p>
          </div>
        </div>

        <div className="container">
          <div className="contact-page__contact-list">
            <div className="row">
              <div className="col-xl-4 col-lg-4">
                <div className="contact-page__contact-list-single">
                  <div className="contact-page__contact-list-icon">
                    <span className="icon-call" aria-hidden="true" />
                  </div>
                  <div className="contact-page__contact-list-content">
                    <h3>Telefoon &amp; WhatsApp</h3>
                    <p><a href="tel:+31616136942">+31 6 16 13 69 42</a></p>
                    <p><a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer">WhatsApp ons</a></p>
                  </div>
                </div>
              </div>
              <div className="col-xl-4 col-lg-4">
                <div className="contact-page__contact-list-single">
                  <div className="contact-page__contact-list-icon">
                    <span className="icon-envelope" aria-hidden="true" />
                  </div>
                  <div className="contact-page__contact-list-content">
                    <h3>E-mail</h3>
                    <p><a href="mailto:info@art-wer.nl">info@art-wer.nl</a></p>
                    <p>Werkgebied: Flevoland &amp; Midden-Nederland</p>
                  </div>
                </div>
              </div>
              <div className="col-xl-4 col-lg-4">
                <div className="contact-page__contact-list-single">
                  <div className="contact-page__contact-list-icon">
                    <span className="icon-location" aria-hidden="true" />
                  </div>
                  <div className="contact-page__contact-list-content">
                    <h3>Adres</h3>
                    <p>
                      <a
                        href="https://www.google.com/maps/dir/?api=1&destination=Lucernehof+15,+8256+CH+Biddinghuizen"
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Routebeschrijving naar Lucernehof 15, 8256 CH Biddinghuizen (opent in een nieuw tabblad)"
                      >
                        Lucernehof 15<br />8256 CH Biddinghuizen
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactMain;
